import { isLegacyVimeoBlock } from "../../utils/linkPageMedia";
import {
  CONTACT_PLATFORM_OPTIONS,
  LINK_PAGE_ITEM_TYPES,
  LINK_PAGE_THEME_PRESETS,
  SECTION_LAYOUTS,
  SOCIAL_PLATFORM_OPTIONS,
} from "./linkPageBuilderConfig";

export const DEFAULT_LINK_PAGE = {
  draft: {
    items: [],
    theme: { ...LINK_PAGE_THEME_PRESETS[0].theme },
    layout: {
      alignment: "center",
      width: "standard",
      spacing: "normal",
    },
    updatedAt: null,
  },
  published: {
    items: [],
    theme: { ...LINK_PAGE_THEME_PRESETS[0].theme },
    layout: {
      alignment: "center",
      width: "standard",
      spacing: "normal",
    },
    updatedAt: null,
  },
  meta: {
    status: "draft",
    isPublic: true,
    publishedAt: null,
    lastSavedAt: null,
  },
};

export const createId = () => {
  if (typeof window !== "undefined" && window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }

  return `lp_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
};

const sanitizeHref = (value) => {
  if (!value) return "";
  const trimmed = value.trim();
  if (!trimmed) return "";
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
};

// YYYY-MM-DD in the viewer's own calendar, as event dates are entered.
export const localIsoDate = (date = new Date()) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

const isoDateInDays = (days) => {
  const date = new Date();
  date.setDate(date.getDate() + days);
  return localIsoDate(date);
};

// The name and bio a link page shows, shares and announces: its own header first, then the profile.
export const linkPageOwnerName = (profile) =>
  profile?.channelName || [profile?.name, profile?.surname].filter(Boolean).join(" ");
export const linkPageHeading = (version, profile) =>
  version?.profile?.displayName?.trim() || linkPageOwnerName(profile);
export const linkPageBio = (version, profile) =>
  version?.profile?.bio?.trim() || profile?.aboutMe || "";

const CONTENT_BLOCK_DEFAULTS = {
  ticker: () => ({ title: "News line", links: [{ id: createId(), title: "Your news goes here" }] }),
  reactions: () => ({ title: "Reactions", value: "👏,🔥,❤️,🎧" }),
  events: () => ({
    title: "Events",
    links: [{ id: createId(), title: "City", subtitle: "Venue", date: isoDateInDays(14), url: "", soldOut: false }],
  }),
  articles: () => ({ title: "Latest on BonJourno", value: "" }),
};

export const createItemByType = (type) => {
  const id = createId();
  if (CONTENT_BLOCK_DEFAULTS[type])
    return {
      id,
      type,
      subtitle: "",
      url: "",
      value: "",
      platform: "",
      icon: "",
      thumbnail: "",
      variant: "button",
      isVisible: true,
      sortOrder: 0,
      links: [],
      sectionId: "",
      column: 0,
      ...CONTENT_BLOCK_DEFAULTS[type](),
    };
  if (["text", "image", "video", "socialRow", "booking"].includes(type))
    return {
      id,
      type,
      title: {
        text: "Text",
        image: "Image",
        video: "Video",
        socialRow: "Socials",
        booking: "Book Me",
      }[type],
      subtitle: "",
      url: "",
      value: "",
      platform: "",
      icon: "",
      thumbnail: "",
      variant: "button",
      isVisible: true,
      sortOrder: 0,
      links: [],
      playOnPage: true,
    };

  switch (type) {
    case LINK_PAGE_ITEM_TYPES.SOCIAL:
      return {
        id,
        type,
        title: "Social link",
        subtitle: "",
        url: "",
        value: "",
        platform: "instagram",
        icon: "",
        thumbnail: "",
        variant: "button",
        isVisible: true,
        sortOrder: 0,
      };
    case LINK_PAGE_ITEM_TYPES.CONTACT:
      return {
        id,
        type,
        title: "Contact me",
        subtitle: "",
        url: "",
        value: "",
        platform: "email",
        icon: "",
        thumbnail: "",
        variant: "button",
        isVisible: true,
        sortOrder: 0,
      };
    case LINK_PAGE_ITEM_TYPES.LINK:
    default:
      return {
        id,
        type: LINK_PAGE_ITEM_TYPES.LINK,
        title: "New link",
        subtitle: "",
        url: "",
        value: "",
        platform: "",
        icon: "",
        thumbnail: "",
        variant: "button",
        isVisible: true,
        sortOrder: 0,
      };
  }
};

export const normalizeLinkPageVersion = (version) => {
  const source = version || {};
  const draftDefaults = DEFAULT_LINK_PAGE.draft;
  const items = Array.isArray(source.items) ? source.items : [];
  const theme = source.theme || {};

  const normalizedItems = items
      .map((item, index) => ({
        ...createItemByType(item?.type || LINK_PAGE_ITEM_TYPES.LINK),
        ...item,
        // Stored sections use the canonical Text block without losing their copy.
        type: item?.type === "section" ? "text" : isLegacyVimeoBlock(item) ? "link" : item?.type || "link",
        ...(isLegacyVimeoBlock(item) ? { variant: "card" } : {}),
        id: item?.id || createId(),
        sortOrder: Number.isFinite(item?.sortOrder) ? item.sortOrder : index,
        url: item?.url ? sanitizeHref(item.url) : "",
      }))
      .sort((left, right) => left.sortOrder - right.sortOrder)
      .map((item, index) => ({
        ...item,
        sortOrder: index,
        column: Number.isInteger(item.column) ? item.column : 0,
      }));
  return {
    sections: normalizeSections(source.sections, normalizedItems),
    profile: {
      displayName: source.profile?.displayName || "",
      bio: source.profile?.bio || "",
    },
    items: normalizedItems,
    theme: {
      ...draftDefaults.theme,
      ...theme,
      presetKey: theme.presetKey || (Object.keys(theme).length ? "" : "site-default"),
    },
    layout: {
      alignment: source.layout?.alignment || draftDefaults.layout.alignment,
      width: source.layout?.width || draftDefaults.layout.width,
      spacing: source.layout?.spacing || draftDefaults.layout.spacing,
    },
    bookingMode: source.bookingMode === "blocks" ? "blocks" : "auto",
    updatedAt: source.updatedAt || null,
  };
};

export const DEFAULT_SECTION_ID = "main";
const SECTION_COLUMNS = Object.fromEntries(SECTION_LAYOUTS.map((layout) => [layout.value, layout.columns]));
export const sectionColumns = (section) => SECTION_COLUMNS[section?.layout] || 1;

// Mirrors the server: pages saved before sections become one Boxed section.
export const normalizeSections = (sections, items) => {
  const normalized = (Array.isArray(sections) ? sections : [])
    .filter((section) => section?.id)
    .map((section) => ({
      id: section.id,
      name: section.name || "",
      layout: SECTION_COLUMNS[section.layout] ? section.layout : "boxed",
      showProfile: section.showProfile === true && section.layout === "full",
      isVisible: section.isVisible !== false,
    }));
  if (!normalized.length)
    normalized.push({ id: DEFAULT_SECTION_ID, name: "Links", layout: "boxed", showProfile: false, isVisible: true });
  const ids = new Set(normalized.map((section) => section.id));
  items.forEach((item) => {
    if (!ids.has(item.sectionId)) item.sectionId = normalized[0].id;
    const section = normalized.find((entry) => entry.id === item.sectionId);
    item.column = Math.max(0, Math.min(item.column || 0, sectionColumns(section) - 1));
  });
  return normalized;
};

export const createSection = (layout = "boxed", name = "") => ({
  id: createId(),
  name,
  layout,
  showProfile: false,
  isVisible: true,
});

export const normalizeLinkPage = (linkPage) => {
  const source = linkPage || {};

  return {
    draft: normalizeLinkPageVersion(source.draft),
    published: normalizeLinkPageVersion(source.published),
    meta: {
      ...DEFAULT_LINK_PAGE.meta,
      ...(source.meta || {}),
    },
  };
};

export const getLinkPageVersionForAudience = (
  linkPage,
  audience = "public"
) => {
  const normalized = normalizeLinkPage(linkPage || DEFAULT_LINK_PAGE);
  const hasDraftItems = normalized.draft.items.length > 0;
  const hasPublishedItems = normalized.published.items.length > 0;

  if (audience === "owner") {
    if (hasDraftItems) return normalized.draft;
    if (hasPublishedItems) return normalized.published;
    return normalized.draft;
  }

  if (normalized.meta.isPublic === false) {
    return null;
  }

  return normalized.published;
};

// A copy with fresh ids for the block and each of its rows.
export const cloneItem = (item, patch = {}) => ({
  ...item,
  id: createId(),
  links: item.links?.map((link) => ({ ...link, id: createId() })),
  ...patch,
});

export const reorderItems = (items) =>
  items.map((item, index) => ({
    ...item,
    sortOrder: index,
  }));

export const moveItemByIds = (items, activeId, overId) => {
  const oldIndex = items.findIndex((item) => item.id === activeId);
  const newIndex = items.findIndex((item) => item.id === overId);

  if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) {
    return reorderItems(items);
  }

  const next = [...items];
  const [moved] = next.splice(oldIndex, 1);
  next.splice(newIndex, 0, moved);

  return reorderItems(next);
};

export const getSocialPlatformMeta = (platform) =>
  SOCIAL_PLATFORM_OPTIONS.find((item) => item.value === platform) ||
  SOCIAL_PLATFORM_OPTIONS[SOCIAL_PLATFORM_OPTIONS.length - 1];

export const getContactPlatformMeta = (platform) =>
  CONTACT_PLATFORM_OPTIONS.find((item) => item.value === platform) ||
  CONTACT_PLATFORM_OPTIONS[0];

export const getContactHref = (item) => {
  const value = item.value?.trim() || "";
  if (!value) return "";

  switch (item.platform) {
    case "email":
      return `mailto:${value}`;
    case "phone":
      return `tel:${value.replace(/\s+/g, "")}`;
    case "whatsapp":
      return `https://wa.me/${value.replace(/[^\d]/g, "")}`;
    case "telegram":
      return `https://t.me/${value.replace(/^@/, "")}`;
    default:
      return "";
  }
};

export const getItemHref = (item) => {
  if (item.type === LINK_PAGE_ITEM_TYPES.CONTACT) {
    return getContactHref(item);
  }

  if (item.type === LINK_PAGE_ITEM_TYPES.TEXT) {
    return "";
  }

  return sanitizeHref(item.url);
};

export const getRenderableItems = (version) =>
  reorderItems(
    (Array.isArray(version?.items) ? version.items : [])
      .filter((item) => item.isVisible)
      .sort((left, right) => left.sortOrder - right.sortOrder)
  );

export const getItemDescription = (item) => {
  if (item.type === LINK_PAGE_ITEM_TYPES.CONTACT) {
    return item.value || item.subtitle || "";
  }

  if (item.type === LINK_PAGE_ITEM_TYPES.SOCIAL) {
    return item.subtitle || "";
  }

  return item.subtitle || "";
};

export const getThemeStyle = (theme) => {
  const source = theme || DEFAULT_LINK_PAGE.draft.theme;
  // Historical presets carry complete saved colors; do not recolor those records.
  const knownPreset = !source.presetKey || ["paper", "after-dark", "golden-hour"].includes(source.presetKey)
    || LINK_PAGE_THEME_PRESETS.some((preset) => preset.key === source.presetKey);
  const colorValid = (value) => typeof value === "string" && /^(#(?:[\da-f]{3}|[\da-f]{4}|[\da-f]{6}|[\da-f]{8})|rgba?\(\s*\d+(?:\.\d+)?%?\s*,\s*\d+(?:\.\d+)?%?\s*,\s*\d+(?:\.\d+)?%?(?:\s*,\s*(?:0|1|0?\.\d+))?\s*\))$/i.test(value);
  const useSite = source.presetKey === "site-default" || !knownPreset
    || !colorValid(source.backgroundColor) || !colorValid(source.textColor)
    || (source.backgroundStyle === "gradient" && !colorValid(source.backgroundAccentColor));
  const base = useSite ? {
    ...DEFAULT_LINK_PAGE.draft.theme,
    backgroundColor: "var(--theme-color-bg-page)",
    surfaceColor: "var(--theme-color-bg-elevated)",
    textColor: "var(--theme-color-text)",
    mutedTextColor: "var(--theme-color-text-muted)",
    buttonTextColor: "var(--theme-color-white)",
    buttonBackgroundColor: "var(--theme-color-primary)",
    buttonOutlineColor: "var(--theme-color-border-strong)",
    profileCardBackgroundColor: "var(--theme-color-bg-elevated)",
  } : { ...DEFAULT_LINK_PAGE.draft.theme, ...source };
  const profileCardBackground =
    base.profileCardBackgroundColor || "rgba(255, 255, 255, 0.08)";

  return {
    "--lp-background":
      base.backgroundStyle === "image" && base.backgroundImage
        ? `linear-gradient(rgb(0 0 0 / ${Math.min(
            90,
            Math.max(0, base.backgroundOverlay ?? 30)
          )}%), rgb(0 0 0 / ${Math.min(
            90,
            Math.max(0, base.backgroundOverlay ?? 30)
          )}%)), url(${JSON.stringify(base.backgroundImage)}) center ${
            base.backgroundPosition ?? 50
          }% / cover no-repeat`
        : base.backgroundStyle === "gradient"
        ? `linear-gradient(160deg, ${base.backgroundColor} 0%, ${base.backgroundAccentColor} 100%)`
        : base.backgroundColor,
    "--lp-surface": base.surfaceColor,
    "--lp-text": base.textColor,
    "--lp-muted": base.mutedTextColor,
    "--lp-button-text": base.buttonTextColor,
    "--lp-button-background": base.buttonBackgroundColor,
    "--lp-button-outline": base.buttonOutlineColor,
    "--lp-button-shadow": base.buttonShadow,
    "--lp-radius": `${base.buttonRadius}px`,
    "--lp-font": base.fontFamily,
    "--lp-profile-card-background": profileCardBackground,
    "--lp-profile-card-text": base.textColor,
    "--lp-profile-card-muted": base.mutedTextColor,
    "--lp-profile-card-avatar-border": base.buttonOutlineColor,
    "--lp-profile-card-outline": base.buttonOutlineColor,
  };
};

// The profile theme sets only the background and text colors of the profile and dashboard.
export const profileThemeVars = (profile) => {
  const vars = getThemeStyle(getProfileTheme(profile));
  return {
    "--lp-background": vars["--lp-background"],
    "--lp-text": vars["--lp-text"],
    "--lp-muted": vars["--lp-muted"],
    "--lp-surface": vars["--lp-surface"],
  };
};

export const getProfileTheme = (profile) => normalizeLinkPageVersion({
  theme: profile?.profileTheme || profile?.linkPage?.published?.theme || profile?.linkPage?.draft?.theme,
}).theme;
